/**
 * API Client for Webhooks Management
 * Handles HTTP requests for webhook monitoring and statistics
 */
import api from './api';

export interface WebhookEvent {
  id: string;
  provider: string;
  eventId: string;
  eventType: string;
  processed: boolean;
  processedAt?: string;
  retryCount: number;
  createdAt: string;
  tenantId?: string;
  companyId?: string;
}

export interface WebhookEventDetails extends WebhookEvent {
  payload: Record<string, any>;
  headers?: Record<string, any>;
  processingError?: string;
  paymentTransactionId?: string;
  Tenant?: {
    id: string;
    name: string;
  };
  Company?: {
    id: string;
    name: string;
  };
}

export interface WebhookStatistics {
  total: number;
  processed: number;
  pending: number;
  failed: number;
  failureRate: number;
  avgProcessingTime?: number;
  byProvider: Array<{
    provider: string;
    total: number;
    processed: number;
    failed: number;
  }>;
  byEventType: Array<{
    eventType: string;
    count: number;
  }>;
}

export interface WebhookHealth {
  healthy: boolean;
  issues: string[];
  timestamp: string;
}

export interface GetWebhookEventsParams {
  provider?: 'paypal' | 'stripe';
  processed?: boolean;
  page?: number;
  limit?: number;
}

export interface WebhookEventsResponse {
  success: boolean;
  data: WebhookEvent[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
}

export interface WebhookStatsResponse {
  success: boolean;
  data: WebhookStatistics;
  filters: {
    provider: string;
    startDate: string | null;
    endDate: string | null;
  };
}

export interface WebhookEventDetailsResponse {
  success: boolean;
  data: WebhookEventDetails;
}

export interface WebhookHealthResponse {
  success: boolean;
  healthy: boolean;
  issues: string[];
  timestamp: string;
}

export interface FailedWebhooksResponse {
  success: boolean;
  data: WebhookEventDetails[];
  count: number;
  limit: number;
}

/**
 * API client for Webhooks Management
 */
export const webhooksAPI = {
  /**
   * Get webhook statistics
   */
  async getStatistics(
    provider?: 'paypal' | 'stripe',
    startDate?: string,
    endDate?: string
  ): Promise<WebhookStatistics> {
    const queryParams = new URLSearchParams();
    if (provider) queryParams.append('provider', provider);
    if (startDate) queryParams.append('startDate', startDate);
    if (endDate) queryParams.append('endDate', endDate);

    const response = await api.get<WebhookStatsResponse>(
      `/payments/webhooks/monitoring/stats?${queryParams.toString()}`
    );
    return response.data;
  },

  /**
   * Get webhook events (paginated)
   */
  async getEvents(params?: GetWebhookEventsParams): Promise<{
    events: WebhookEvent[];
    pagination: any;
  }> {
    const queryParams = new URLSearchParams();
    if (params?.provider) queryParams.append('provider', params.provider);
    if (params?.processed !== undefined)
      queryParams.append('processed', params.processed.toString());
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());

    const response = await api.get<WebhookEventsResponse>(
      `/payments/webhooks/monitoring/events?${queryParams.toString()}`
    );
    return {
      events: response.data,
      pagination: response.pagination,
    };
  },

  /**
   * Get webhook event details by ID
   */
  async getEventDetails(id: string): Promise<WebhookEventDetails> {
    const response = await api.get<WebhookEventDetailsResponse>(
      `/payments/webhooks/monitoring/events/${id}`
    );
    return response.data;
  },

  /**
   * Get failed webhooks for manual review
   */
  async getFailedWebhooks(limit: number = 50): Promise<WebhookEventDetails[]> {
    const response = await api.get<FailedWebhooksResponse>(
      `/payments/webhooks/monitoring/failed?limit=${limit}`
    );
    return response.data;
  },

  /**
   * Check webhook processing health
   */
  async getHealth(): Promise<WebhookHealth> {
    const response = await api.get<WebhookHealthResponse>('/payments/webhooks/monitoring/health');

    // Le backend retourne directement healthy, issues, timestamp au niveau racine
    return {
      healthy: response.healthy ?? false,
      issues: response.issues || [],
      timestamp: response.timestamp || new Date().toISOString(),
    };
  },
};
